import { styled } from '../../modules';

type StatusTone = 'pending' | 'progress' | 'done' | 'declined' | 'terminal';

const TONE_COLORS: Record<StatusTone, { bg: string; fg: string }> = {
    pending: { bg: '#edf2f7', fg: '#718096' },
    progress: { bg: '#fef5e7', fg: '#b7791f' },
    done: { bg: '#e6f4ea', fg: '#2f855a' },
    declined: { bg: '#fdecea', fg: '#c53030' },
    terminal: { bg: '#edf2f7', fg: '#4a5568' },
};

export const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    background-color: #ffffff;
    overflow: hidden;
`;

export const LogoSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e5e5;
    flex-shrink: 0;
`;

export const LogoImage = styled.img`
    max-height: 52px;
    max-width: 200px;
    object-fit: contain;
`;

export const ScrollContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    width: 100%;
    overflow-y: auto;
    padding: 28px 20px 24px;
    gap: 20px;
`;

export const HeaderBlock = styled.div`
    width: 100%;
`;

export const Eyebrow = styled.div`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.4px;
    color: #718096;
    text-transform: uppercase;
`;

export const Title = styled.h1<{ $color: string }>`
    font-size: 23px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    margin: 6px 0 0;
    line-height: 1.2;
    letter-spacing: -0.2px;
`;

export const Subtitle = styled.p`
    font-size: 14px;
    color: #4a5568;
    margin: 8px 0 0;
    line-height: 1.5;
`;

export const ProgressBlock = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const ProgressRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const ProgressLabel = styled.span`
    font-size: 13px;
    color: #718096;
`;

export const ProgressCount = styled.span<{ $color: string }>`
    font-size: 13px;
    color: #718096;

    strong {
        color: ${({ $color }) => $color};
        font-weight: 700;
    }
`;

export const ProgressTrack = styled.div`
    width: 100%;
    height: 4px;
    border-radius: 999px;
    background-color: #edf2f7;
    overflow: hidden;
`;

export const ProgressFill = styled.div<{ $color: string; $pct: number }>`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background-color: ${({ $color }) => $color};
    border-radius: 999px;
    transition: width 0.3s ease;
`;

export const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

export const ListItem = styled.button<{ $color: string }>`
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 14px 16px;
    border: 1px solid #edf2f7;
    border-radius: 14px;
    background-color: #ffffff;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: ${({ $color }) => $color}55;
    }
`;

export const ItemTitle = styled.span`
    display: block;
    width: 100%;
    font-size: 14.5px;
    font-weight: 700;
    color: #2d3748;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ItemMetaRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
`;

export const ItemMetaRight = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
`;

export const ItemSummary = styled.span`
    font-size: 12.5px;
    color: #718096;
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const StatusBadge = styled.span<{ $tone: StatusTone }>`
    flex-shrink: 0;
    font-size: 11.5px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 999px;
    text-transform: lowercase;
    background-color: ${({ $tone }) => TONE_COLORS[$tone].bg};
    color: ${({ $tone }) => TONE_COLORS[$tone].fg};
`;

export const Chevron = styled.span<{ $color: string }>`
    color: ${({ $color }) => $color};
    display: flex;
    flex-shrink: 0;
`;

export const EmptyState = styled.div`
    width: 100%;
    text-align: center;
    color: #718096;
    font-size: 14px;
    padding: 32px 0;
`;

export const Footer = styled.footer`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 20px;
    border-top: 1px solid #e5e5e5;
    background-color: #ffffff;
    flex-shrink: 0;
`;

export const CommitButton = styled.button<{ $color: string; $disabled: boolean }>`
    width: 100%;
    box-sizing: border-box;
    border: none;
    border-radius: 10px;
    padding: 16px 20px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 700;
    color: #ffffff;
    background-color: ${({ $color, $disabled }) => ($disabled ? `${$color}59` : $color)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background-color 0.2s ease;
`;
